import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tickerDetailService } from '../services/tickerDetailService';
import { marketDataService } from '../services/marketDataService';
import { marketMoversService } from '../services/marketMoversService';
import { fznService } from '../services/fznService';
import { usePermissions } from '../hooks/usePermissions';
import { getViewPermissionConfig } from '../config/permissions';
import { fmtAlertTime, fmtUsDate, fmtRefPrice } from '../components/TickerDetail/format';
import { TZ } from '../utils/marketTime';
import { isMarketOpen } from '../utils/marketCalendar';
import { fetchIndexStats } from '../services/indexStatsService';
import { useDailyBars } from '../components/TickerDetail/useDailyBars';
import { fiveDayChangePct } from '../utils/fiveDayChange';
import { SqueezoGauge } from '../components/SqueezoGauge';
import { SectorRotation } from '../components/SectorRotation';
import { AntiVix } from '../components/AntiVix';
import { ratingsService } from '../services/ratingsService';
import type { StockRating } from '../types/ratings';
import { firmDisplayName } from '../utils/ratingFirm';
import { MarketBreadth } from '../components/MarketBreadth';
import type { MarketBreadthData } from '../services/marketBreadthService';
import { PanelUnavailable } from '../components/PanelMessage';
import type { MarketMoverView } from '../types/marketMovers';
import { todayEt } from '../utils/marketTime';
import {
  fetchOiChanges,
  isOiChangesUnavailable,
  rankOiChanges,
  type OiChangeFilter,
} from '../services/oiChangesService';
import { getOptionsDisplayTicker } from '../utils/tickerDisplay';
import {
  flowRowKey,
  fmtStrike,
  MARKET_TOP_FLOW_LIMIT,
  rankLargestFlowToday,
} from './marketTopFlow';

/**
 * Market Overview — the state of the tape in one screen.
 *
 * Everything here is market-wide; nothing is scoped to a ticker. The layout
 * runs widest-lens first: index levels, then what is moving, then where the
 * money went, then how broad the move is.
 */

// The four the desk watches. SPX is deliberately not here — it has no quote
// endpoint of its own, and SPY is the tradable proxy the levels are published
// against anyway.
const INDICES = ['SPY', 'QQQ', 'DIA', 'IWM'] as const;

/**
 * One cadence for the whole page.
 *
 * Every panel here refreshes together so the timestamp in the bar is true of
 * the screen rather than of whichever card happened to update last. Polling
 * stops outside the session — none of these numbers move once the bell has
 * gone, so an overnight interval spends requests to receive yesterday back.
 *
 * ⚠️ This is how often the CLIENT asks, not how fresh the answer is. Several
 * of these routes are Dave's and may cache behind them; only `market-movers`
 * documents its interval (Redis, 3 min). Matching this number to his actual
 * refresh needs him to say what it is — see the handoff open items.
 */
const PAGE_REFRESH_MS = 5 * 60 * 1000;

/* ── shared chrome ──────────────────────────────────────────────────── */

function fmtPrice(n: number | string | null | undefined): string {
  const v = Number(n);
  if (n == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Signed percentage.
 *
 * Coerces first: these feeds declare `number` in the types and hand back
 * strings on the wire, and `.toFixed` does not exist on a string — it throws
 * at runtime while typechecking cleanly. See docs/FUZION_UI_HANDOFF.md §10.
 */
function fmtPct(n: number | string | null | undefined): string {
  const v = Number(n);
  if (n == null || !Number.isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

/** True when the value is a real, positive-or-zero number. */
function isUp(n: number | string | null | undefined): boolean {
  return Number(n) >= 0;
}

function Panel({ title, meta, fill, children }: {
  title: string;
  meta?: React.ReactNode;
  /**
   * Make the body take the leftover height instead of sizing to its content.
   *
   * Grid items already stretch, so a short panel beside a tall one is already
   * full height — but its CONTENT is not, which is why a scrollable child needs
   * this. `min-h-0` is the load-bearing half: without it a flex child refuses to
   * shrink below its content and the inner `overflow-y-auto` never engages,
   * pushing the card taller than its neighbour instead of scrolling.
   */
  fill?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section
      className={`p-4 rounded-lg border border-dark-border bg-dark-surface ${
        fill ? 'flex flex-col min-h-0 h-full xl:min-h-[22rem]' : ''
      }`}
    >
      <div
        className={`flex items-baseline gap-3 pb-2 mb-3 flex-wrap border-b border-dark-border ${
          fill ? 'flex-none' : ''
        }`}
      >
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">{title}</h2>
        {meta}
      </div>
      {/* `relative` so a `fill` panel can absolutely position its body. That is
          what stops tall content driving the grid row: an absolute child
          contributes nothing to intrinsic height, so the row is sized by the
          panel BESIDE it and this one scrolls to fit. */}
      {fill ? <div className="relative flex-1 min-h-0">{children}</div> : children}
    </section>
  );
}

/**
 * Defensive fallback for a panel whose entitlement is missing.
 *
 * Carries NO upgrade link: this page is open in full to every tier that can
 * reach it, so anyone seeing it has already passed the route's `ViewGate`.
 * Offering an upsell here would advertise a purchase that buys nothing extra
 * on this page.
 */
function Locked({ what }: { what: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-300 py-2">
      <svg className="w-4 h-4 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="4" y="11" width="16" height="9" rx="2" />
        <path d="M8 11V7a4 4 0 018 0v4" strokeLinecap="round" />
      </svg>
      <span>{what} is unavailable for your plan.</span>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-gray-300 text-sm">{children}</p>;
}

/**
 * The state a panel shows when its endpoint answers nothing useful.
 *
 * Deliberately not red — see the note in `PanelMessage`. A page where several
 * feeds are still unbuilt would otherwise be a wall of alarm colour, which
 * teaches a reader to ignore exactly the colour that means "down" everywhere
 * else on the screen.
 */
function Unavailable({ what }: { what: string }): React.ReactElement {
  return <PanelUnavailable what={what} />;
}

const HEAD = 'px-3 py-2 font-semibold text-gray-400 whitespace-nowrap text-center';
const CELL = 'px-3 py-1.5 whitespace-nowrap text-center';

/* ── index rail ─────────────────────────────────────────────────────── */

/**
 * One index, reduced to where it is and how far it has moved.
 *
 * ⚠️ **Deliberately only the quote and the 5-day move.** This tile previously
 * also carried a 52-week range meter, the major gamma corridor, share volume
 * and net options premium. Those were removed by request — the rail is the
 * page's widest lens and was answering questions the panels below it answer
 * better.
 *
 * The 5D badge reads the daily bars this tile already fetches. It previously
 * showed a hardcoded constant per symbol — invented figures sitting beside the
 * live quote with nothing marking them as fabricated. Removed 2026-08-21.
 *
 * `useDailyBars` fetches 252 sessions, far more than the six a five-session
 * move needs. That window is sized by Ticker Explorer's 52-week range and
 * shared through the same query key, so opening one of these symbols there
 * costs nothing. Narrowing it here would split the key and double the requests.
 */
function IndexTile({ symbol }: { symbol: string }): React.ReactElement {
  const { data } = useQuery({
    queryKey: ['tickerQuote', symbol],
    queryFn: () => tickerDetailService.getQuote(symbol),
    // Everything on this page moves together on a five-minute clip, so the
    // timestamp in the bar is true of the whole screen rather than of
    // whichever panel happened to refresh last.
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
    enabled: !!symbol,
  });

  // Shares Ticker Explorer's query key, so opening one of these symbols there
  // afterwards costs nothing.
  const { bars } = useDailyBars(symbol);

  const quote = data?.data;
  const up = isUp(quote?.change);

  // null until six sessions have loaded — the badge hides rather than printing
  // a 0.0% that would read as "unchanged over five sessions".
  const fiveDay = fiveDayChangePct(bars);

  return (
    /* Deliberately tight, by request. Four of these share half the page width
       and the column carries the Anti-VIX card beneath them, so the tile is
       sized to be read at a glance and then get out of the way. */
    <div className="p-2.5">
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-[11px] font-bold text-accent-yellow tracking-wider">{symbol}</span>

        {/* Five-session move, as a pill so it reads as a different measurement
            from the day's change below rather than a second opinion on it.
            Costs no request — `useDailyBars` is already fetched for this tile. */}
        {fiveDay != null && (
          <span
            className={`flex items-baseline gap-1 px-1.5 py-0.5 rounded border font-numeric text-[10px] leading-none whitespace-nowrap ${
              fiveDay >= 0
                ? 'border-bull-blue/40 bg-bull-blue/10 text-bull-blue'
                : 'border-bear-red/40 bg-bear-red/10 text-bear-red'
            }`}
          >
            {/* Text Muted, not a dimmed copy of the direction colour: "5D"
                names the window and carries no direction itself. Only the
                percentage should be blue or red. */}
            <span className="font-bold tracking-wider text-gray-400">5D</span>
            {fmtPct(fiveDay)}
          </span>
        )}
      </div>

      {/* Price left, the day's move right — so the two change figures sit
          under the 5D badge above them and the tile has one right-hand column
          of percentages to scan down, rather than numbers trailing off the
          price at whatever width it happens to be. */}
      <div className="flex items-baseline justify-between gap-1.5 mt-1">
        <span className="font-numeric text-white font-bold text-base leading-none">
          {quote ? fmtPrice(quote.price) : '—'}
        </span>

        <span className="flex items-baseline gap-1.5 flex-none whitespace-nowrap">
          <span className={`font-numeric text-xs leading-none ${up ? 'text-bull-blue' : 'text-bear-red'}`}>
            {quote ? `${isUp(quote.change) ? '+' : ''}${fmtPrice(quote.change)}` : ''}
          </span>
          <span className={`font-numeric text-xs leading-none ${up ? 'text-bull-blue' : 'text-bear-red'}`}>
            {quote ? fmtPct(quote.changePercentage) : '—'}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * The four index tiles.
 *
 * ⚠️ **DAVE — two requests below are now UNREAD, and were deliberately left
 * running rather than removed.**
 *
 * The tiles were reduced to symbol, price, point change and percentage. That
 * deleted the only consumers of both queries in this component:
 *
 *  • `steamroomLevels` (`fetchComboData`) fed the major gamma corridor
 *  • `indexStats` (`fetchIndexStats`) fed the Net Prem stat
 *
 * Both still fire on the page's five-minute clip and still populate the React
 * Query cache; nothing on this page renders either any more. They are kept
 * because pulling a request is a data-flow decision, not a layout one — and
 * because the cache entries may be serving something outside this file. **Your
 * call whether they go.**
 *
 * `hasLevels` is likewise untouched: it gates the `steamroomLevels` request
 * behind the Fuzion Levels entitlement, so removing it would change who issues
 * that call. If the query goes, this prop and its call site go with it.
 *
 * ⚠️ The results are intentionally not destructured. Binding them would leave
 * unused locals, which `noUnusedLocals` rejects — calling the hook bare keeps
 * the request firing without pretending anything reads the answer.
 */
function IndexRail({ hasLevels }: { hasLevels: boolean }): React.ReactElement {
  // One request covers all four tiles' walls. UNREAD — see above.
  useQuery({
    queryKey: ['steamroomLevels', [...INDICES]],
    queryFn: () => marketDataService.fetchComboData([...INDICES]),
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
    enabled: hasLevels,
  });

  // Also one request for all four — QF's watchlist-quotes is a batch endpoint
  // taking up to 64 symbols, so this does not grow with the number of tiles.
  // UNREAD — see above.
  useQuery({
    queryKey: ['indexStats'],
    queryFn: fetchIndexStats,
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
  });

  return (
    /* One card, four tiles. Same rule as the rest of the page: the section is
       the object, the tiles are parts of it. */
    <div className="rounded-lg border border-dark-border bg-dark-surface grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-dark-border">
      {INDICES.map((symbol) => (
        <IndexTile key={symbol} symbol={symbol} />
      ))}
    </div>
  );
}

/* ── movers ─────────────────────────────────────────────────────────── */

const MOVER_VIEWS: [MarketMoverView, string][] = [
  ['gainers', 'Gainers'],
  ['losers', 'Losers'],
  ['active', 'Most Active'],
];

function Movers({ hasAccess }: { hasAccess: boolean }): React.ReactElement {
  const [view, setView] = useState<MarketMoverView>('gainers');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketMovers', view],
    queryFn: () => marketMoversService.fetchMovers(view),
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
    enabled: hasAccess,
  });

  // Array.isArray, not `?? []`: a wrapped or unexpected payload would make
  // .slice throw rather than simply render nothing.
  const rows = (Array.isArray(data?.data) ? data.data : []).slice(0, 12);

  return (
    <Panel
      title="Market Movers"
      meta={
        <div className="flex text-xs rounded border border-dark-border overflow-hidden ml-auto">
          {MOVER_VIEWS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`px-3 py-1 transition-colors ${
                view === key
                  ? 'bg-accent-yellow text-hot-tag-text font-semibold'
                  : 'bg-dark-surface text-gray-400 hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      {!hasAccess ? (
        <Locked what="Market Movers" />
      ) : isLoading ? (
        <Note>Loading&hellip;</Note>
      ) : isError ? (
        <Unavailable what="Market movers" />
      ) : !rows.length ? (
        <Note>No movers reported</Note>
      ) : (
        <div className="rounded border border-dark-border overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th className={`${HEAD} text-left`}>Symbol</th>
                <th className={`${HEAD} text-left`}>Name</th>
                <th className={HEAD}>Price</th>
                <th className={HEAD}>Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const up = isUp(m.changesPercentage);
                return (
                  <tr
                    key={m.symbol}
                    className="border-b border-dark-border/40 hover:bg-dark-surface-light transition-colors"
                  >
                    <td className={`${CELL} text-left font-semibold text-accent-yellow`}>
                      {m.symbol}
                    </td>
                    <td className="px-3 py-1.5 text-gray-200 truncate max-w-[200px]">{m.name}</td>
                    <td className={`${CELL} font-numeric text-white`}>{fmtPrice(m.price)}</td>
                    <td className={`${CELL} font-numeric font-medium ${up ? 'text-bull-blue' : 'text-bear-red'}`}>
                      {fmtPct(m.changesPercentage)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* ── top flow ───────────────────────────────────────────────────────── */

/**
 * The day's biggest bets across the whole market, by premium.
 *
 * Same floored QF history as the Wiseguy widget
 * (`GET /v1/steamroom/alerts/history?date=`). Ranked client-side because that
 * route has no `sort`. The full session is fetched, then ranked — ranking a
 * short page would mean "largest of the most recent few" under a heading that
 * says Largest.
 *
 * `date` is sent explicitly so this panel owns "today", matching the widget.
 * An empty 200 before the first print is a quiet morning, not an error.
 */
function TopFlow({ hasAccess }: { hasAccess: boolean }): React.ReactElement {
  const today = todayEt();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketTopFlow', today],
    queryFn: () =>
      fznService.getAlerts({
        date: today,
        limit: MARKET_TOP_FLOW_LIMIT,
      }),
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
    enabled: hasAccess,
  });

  const rows = useMemo(
    () => rankLargestFlowToday(data?.data ?? []),
    [data]
  );

  // QF caps a response at 5000; our fetch cap is 1000. If either fires, the
  // client sort is ranking a partial session — surfaced rather than silent.
  useEffect(() => {
    if (data?.truncated) {
      console.warn(
        '[MarketOverview] Alert history truncated; Largest Flow Today may be incomplete.'
      );
    }
  }, [data?.truncated]);

  return (
    <Panel
      title="Largest Flow Today"
      // Says what is missing rather than leaving a reader to wonder why SPY is
      // absent from a panel about the day's biggest bets.
      meta={
        hasAccess && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            Excludes ETFs
          </span>
        )
      }
    >
      {!hasAccess ? (
        <Locked what="Wiseguy Alerts" />
      ) : isLoading ? (
        <Note>Loading&hellip;</Note>
      ) : isError ? (
        <Unavailable what="Flow" />
      ) : !rows.length ? (
        <Note>No alerts yet today</Note>
      ) : (
        <div className="rounded border border-dark-border overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th className={HEAD}>Time</th>
                <th className={HEAD}>Symbol</th>
                <th className={HEAD}>Bias</th>
                <th className={HEAD}>Type</th>
                <th className={HEAD}>Ref Price</th>
                <th className={HEAD}>Strike</th>
                <th className={HEAD}>Size</th>
                <th className={HEAD}>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr
                  key={flowRowKey(a)}
                  className={`border-b border-dark-border/40 hover:bg-dark-surface-light transition-colors ${
                    a.top_bet ? 'bg-accent-yellow/5' : ''
                  }`}
                >
                  <td className={`${CELL} text-gray-400`}>{fmtAlertTime(a.received_on)}</td>
                  <td className={`${CELL} font-semibold text-accent-yellow`}>
                    {getOptionsDisplayTicker(a.symbol)}
                  </td>
                  <td
                    className={`${CELL} font-medium ${
                      a.bias === 'Bullish'
                        ? 'text-bull-blue'
                        : a.bias === 'Bearish'
                          ? 'text-bear-red'
                          : 'text-gray-400'
                    }`}
                  >
                    {a.bias || (a.contract_type === 'Call' ? 'Bullish' : 'Bearish')}
                  </td>
                  <td className={CELL}>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] ${
                        a.contract_type === 'Call'
                          ? 'bg-bull-blue/20 text-bull-blue'
                          : 'bg-bear-red/20 text-bear-red'
                      }`}
                    >
                      {a.contract_type}
                    </span>
                  </td>
                  {/* Spot first, then the strike read against it. */}
                  <td className={`${CELL} font-numeric text-gray-300`}>{fmtRefPrice(a.reference_price)}</td>
                  <td className={`${CELL} font-numeric text-white`}>{fmtStrike(a.strike)}</td>
                  <td className={`${CELL} font-numeric text-white`}>{a.bet_display}</td>
                  <td className={`${CELL} font-numeric text-white`}>{fmtUsDate(a.expiry)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/**
 * Analyst upgrades and downgrades, market-wide.
 *
 * Ticker Explorer's `AnalystRatingsSection` answers "what do analysts say about
 * THIS name". This answers "who got re-rated today", which is the question the
 * rest of this page asks — so the two are not duplicates, and retiring the
 * dashboard widget (§7.6) does not conflict with adding this.
 *
 * Source is Dave's portal (`/api/market-data/stock-ratings/`), so this panel
 * costs nothing against the Unusual Whales concurrency cap — see §8 before
 * putting anything beside it that does.
 */
function AnalystActions(): React.ReactElement {
  // Two requests rather than one unsplit page: `action` is a server-side
  // filter, and a single mixed page could come back all upgrades on a strong
  // day, leaving the other column empty for no reason but pagination.
  const upgrades = useQuery({
    queryKey: ['marketRatings', 'upgrade'],
    queryFn: () => ratingsService.fetchRatings({ action: 'upgrade', perPage: 50 }),
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
  });
  const downgrades = useQuery({
    queryKey: ['marketRatings', 'downgrade'],
    queryFn: () => ratingsService.fetchRatings({ action: 'downgrade', perPage: 50 }),
    staleTime: PAGE_REFRESH_MS,
    refetchInterval: () => (isMarketOpen() ? PAGE_REFRESH_MS : false),
  });

  const isLoading = upgrades.isLoading || downgrades.isLoading;
  const isError = upgrades.isError || downgrades.isError;

  /**
   * The endpoint is a rolling feed with no date parameter, so the session is
   * worked out from the data.
   *
   * ⚠️ Deliberately NOT `todayEt()`. Ratings publish through the evening and
   * not at all over a weekend, so a hard "today" filter would leave this panel
   * empty every Monday morning and read as broken. The newest date PRESENT is
   * used instead.
   *
   * ⚠️ That date is no longer shown — the header was asked to lose it — so on a
   * Monday this panel silently shows Friday's actions. Still the right trade
   * against an empty card, but it is now an unlabelled assumption rather than a
   * stated one. Worth restoring the stamp if that ever reads as stale data.
   */
  const { up, down } = useMemo(() => {
    const dayOf = (r: StockRating) => (r.published_date ?? '').slice(0, 10);
    const all = [...(upgrades.data?.results ?? []), ...(downgrades.data?.results ?? [])];
    const newest = all.reduce((max, r) => (dayOf(r) > max ? dayOf(r) : max), '');
    const onDay = (rows: StockRating[] | undefined) =>
      (rows ?? []).filter((r) => dayOf(r) === newest);

    return { up: onDay(upgrades.data?.results), down: onDay(downgrades.data?.results) };
  }, [upgrades.data, downgrades.data]);

  return (
    <Panel title="Analyst Actions" fill>
      {isLoading ? (
        <Note>Loading&hellip;</Note>
      ) : isError ? (
        <Unavailable what="Analyst actions" />
      ) : !up.length && !down.length ? (
        <Note>No rating changes published</Note>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-0 h-full">
          <RatingColumn rows={up} isUpgrade />
          <RatingColumn rows={down} isUpgrade={false} />
        </div>
      )}
    </Panel>
  );
}

/**
 * One side of Analyst Actions.
 *
 * Every row is listed rather than a top-N: on a heavy day the interesting name
 * is as likely to be 20th as 2nd, and a truncated list gives no way to reach
 * it. The column scrolls inside the card instead.
 */
function RatingColumn({ rows, isUpgrade }: {
  rows: StockRating[];
  isUpgrade: boolean;
}): React.ReactElement {
  const tone = isUpgrade ? 'text-bull-blue' : 'text-bear-red';

  return (
    <div className="rounded border border-dark-border overflow-hidden flex flex-col min-h-0">
      {/* flex-none, so the heading stays put while the list moves under it. */}
      <div className="flex-none flex items-baseline gap-3 px-3 py-2 bg-dark-surface-light border-b border-dark-border">
        <span className={`text-[11px] font-semibold uppercase tracking-wider ${tone}`}>
          {isUpgrade ? 'Upgrades' : 'Downgrades'}
        </span>
        {/* Right-aligned against its own list rather than in the panel header:
            the count belongs to the column it sits on. */}
        <span className={`ml-auto font-numeric text-[11px] font-semibold ${tone}`}>
          {rows.length}
        </span>
      </div>

      {!rows.length ? (
        <p className="px-3 py-3 text-xs text-gray-500">None</p>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <table className="w-full text-xs">
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-dark-border last:border-0">
                  {/* Gold, matching every other ticker on this page. */}
                  <td className="px-3 py-1.5 font-semibold text-accent-yellow whitespace-nowrap">
                    {r.ticker}
                  </td>
                  {/* previous → new. The pair is the information; either grade on
                      its own says nothing about which way it moved — so the
                      arrow stays dim and only the grades carry weight. */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className="text-white">{r.previous_grade || '—'}</span>
                    <span className="text-gray-600"> → </span>
                    <span className={tone}>{r.new_grade || '—'}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right text-white truncate max-w-[9rem]">
                    {firmDisplayName(r.grading_company)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── largest OI changes ─────────────────────────────────────────────── */

const OI_FILTERS: ReadonlyArray<{ id: OiChangeFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'increases', label: 'Increases' },
  { id: 'decreases', label: 'Decreases' },
];

/** Rows on screen after the filter. Matches the Ticker Explorer board. */
const OI_LIMIT = 15;

function fmtCount(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function fmtSignedCount(n: number): string {
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}${fmtCount(Math.abs(n))}`;
}

function OiChanges({ hasAccess }: { hasAccess: boolean }): React.ReactElement {
  const [filter, setFilter] = useState<OiChangeFilter>('all');

  // 5 min matches QF Cache-Control. Data settles once ~07:00 ET; polling
  // faster than that gains nothing. No market-hours interval — the roll
  // happens before the open.
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['oiChanges'],
    queryFn: () => fetchOiChanges(),
    staleTime: PAGE_REFRESH_MS,
    enabled: hasAccess,
    retry: 2,
    retryDelay: 1000,
  });

  const rows = useMemo(
    () => rankOiChanges(data?.contracts ?? [], filter, OI_LIMIT),
    [data, filter]
  );

  const dated = Boolean(data?.session_date);
  const dateLabel = data?.session_date
    ? data.prev_session_date
      ? `${fmtUsDate(data.session_date)} vs ${fmtUsDate(data.prev_session_date)}`
      : fmtUsDate(data.session_date)
    : null;

  return (
    <Panel
      title="Largest OI Changes"
      meta={
        <>
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-numeric">
            {dateLabel ?? 'Settle vs prior session'}
          </span>
          <div className="ml-auto flex items-center rounded border border-dark-border overflow-hidden">
            {OI_FILTERS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setFilter(opt.id)}
                aria-pressed={filter === opt.id}
                className={`px-2 py-0.5 text-xs transition-colors ${
                  filter === opt.id
                    ? 'bg-accent-yellow text-hot-tag-text font-semibold'
                    : 'bg-transparent text-gray-400 hover:text-white hover:bg-dark-surface'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      }
    >
      {!hasAccess ? (
        <Locked what="Contract detail" />
      ) : !data && (isLoading || isFetching) ? (
        <Note>Loading&hellip;</Note>
      ) : isError || isOiChangesUnavailable(data) ? (
        <Unavailable what="OI changes" />
      ) : dated && !(data?.contracts.length) ? (
        <Note>Not available yet</Note>
      ) : !rows.length ? (
        <Note>None</Note>
      ) : (
        <div className="overflow-x-auto rounded border border-dark-border">
          <table className="w-full text-xs">
            <thead className="bg-dark-surface-light border-b border-dark-border">
              <tr>
                {['Ticker', 'Type', 'Strike', 'Expiry', 'Prev OI', 'OI Change', 'OI'].map((h) => (
                  <th key={h} className={HEAD}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isCall = r.option_type === 'Call';
                const change = r.oi_change;
                return (
                  <tr
                    key={`${r.symbol}-${r.expiry}-${r.strike}-${r.option_type}`}
                    className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                  >
                    <td className={`${CELL} font-semibold text-accent-yellow`}>
                      {getOptionsDisplayTicker(r.symbol)}
                    </td>
                    <td className={CELL}>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] ${
                          isCall ? 'bg-bull-blue/20 text-bull-blue' : 'bg-bear-red/20 text-bear-red'
                        }`}
                      >
                        {r.option_type}
                      </span>
                    </td>
                    <td className={`${CELL} font-numeric text-white`}>{r.strike.toFixed(2)}</td>
                    <td className={`${CELL} font-numeric text-white`}>{fmtUsDate(r.expiry)}</td>
                    <td className={`${CELL} font-numeric text-white`}>
                      {r.prev_open_interest == null ? '—' : fmtCount(r.prev_open_interest)}
                    </td>
                    <td
                      className={`${CELL} font-numeric ${
                        change == null
                          ? 'text-gray-400'
                          : change > 0
                            ? 'text-bull-blue'
                            : change < 0
                              ? 'text-bear-red'
                              : 'text-gray-400'
                      }`}
                    >
                      {change == null ? '—' : fmtSignedCount(change)}
                    </td>
                    <td className={`${CELL} font-numeric text-white`}>
                      {fmtCount(r.open_interest)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* ── page ───────────────────────────────────────────────────────────── */

/**
 * Session date and when the page's figures were taken, in the bar.
 *
 * One statement for the whole page rather than a timestamp on each panel.
 * Everything here refreshes on the same five-minute clip, so per-panel
 * stamps would repeat the same time four times and still leave the reader
 * unsure whether it applied to the page or only that card.
 *
 * ⚠️ It reports the OLDEST of the panels' timestamps, not the newest. A page
 * is only as fresh as its stalest number, and taking the newest would let one
 * panel that refreshed successfully vouch for three that did not.
 *
 * Reads whatever is already in the react-query cache rather than fetching —
 * it is a label about other requests, and should never be a request itself.
 */
function PageFreshness(): React.ReactElement | null {
  // Reads what MarketBreadth already fetched rather than fetching: this is a
  // label ABOUT other requests and must never be one itself.
  //
  // `getQueryData` rather than a disabled `useQuery`. A disabled query with no
  // queryFn returns `unknown` and needs a cast to be read at all, so a change
  // to the breadth payload would silently blank this stamp instead of failing
  // to compile. Typed against the service's own interface, it cannot drift.
  const breadth = useQueryClient().getQueryData<MarketBreadthData>(['marketBreadth']);

  const capturedAt = breadth?.capturedAt;
  if (!capturedAt) return null;

  const stamp = new Date(capturedAt);
  if (isNaN(stamp.getTime())) return null;

  return (
    <span
      className="ml-auto font-numeric text-[11px] text-gray-400 whitespace-nowrap"
      title="Market data on this page refreshes every five minutes during the session."
    >
      {fmtUsDate(breadth?.date ?? capturedAt)}
      <span className="text-gray-500"> · updated </span>
      {stamp.toLocaleTimeString('en-GB', {
        timeZone: TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}
      {' ET'}
    </span>
  );
}

export function MarketOverviewPage(): React.ReactElement {
  const { hasAnyPermission } = usePermissions();

  /*
   * Panels are entitled by the PAGE, not by the tool that also renders each
   * feed as a dashboard widget.
   *
   * Market Overview is open to Zeroday and FZN Pro in full (2026-09-10), so
   * every panel on it unlocks together. Gating them on their widget
   * `toolType`s instead would re-lock Market Movers (steamroom-only) and Top
   * Flow (steamroom/wsjonly) for Zeroday, which is the state users reported as
   * "not locked but no data".
   *
   * Deliberately NOT fixed by widening those entries in TOOL_PERMISSIONS: the
   * Market Movers and Wiseguy Alerts WIDGETS keep their own, narrower
   * entitlements. A page and a dashboard tile are entitled separately.
   */
  const viewConfig = getViewPermissionConfig('market-overview');
  const hasPageAccess = viewConfig
    ? hasAnyPermission(viewConfig.requiredPermission)
    : true;

  React.useEffect(() => {
    document.title = 'Market Overview — Fuzion';
  }, []);

  return (
    /* flex-1, not h-screen: the AppShell owns viewport height. */
    <div className="flex-1 min-h-0 bg-dark-bg flex flex-col overflow-hidden">
      <div className="flex-none flex items-center gap-3 h-10 px-4 bg-chrome border-b border-dark-border">
        <h1 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Market Overview</h1>
        {/* Beside the title rather than right-aligned: this page has no other
            bar controls to balance against, so a lone gauge across the gap
            read as stranded. */}
        <SqueezoGauge />
        <PageFreshness />
      </div>

      {/* Cards with gaps rather than edge-to-edge bands. Sections read as
          separate objects, which matters on a page where each one is a
          different kind of measurement. */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {/* Breadth left, index levels and Anti-VIX right. Two different
            measurements, so two cards — but the same band of the page, because
            both answer "what happened today" before anything below narrows it
            down. The right card is the taller of the two and sets the row
            height; `MarketBreadth` centres its content to absorb the slack. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <MarketBreadth />

          {/* Two cards stacked in the right column, not one card with two
              halves. Anti-VIX keeps its place directly under the index tiles
              — the reading belongs beside them — but it is a separate object
              with its own border and heading, so the quotes and the
              volatility read as two answers rather than one panel. */}
          <div className="flex flex-col gap-3">
            <IndexRail hasLevels={hasPageAccess} />
            <AntiVix />
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <Movers hasAccess={hasPageAccess} />
          <TopFlow hasAccess={hasPageAccess} />
        </div>

        <SectorRotation />

        {/* Paired rather than stacked, OI on the left. Both answer "who is
            being repositioned" — one by the analysts, one by the option chain —
            so they belong in the same band. Analyst Actions was full width. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 xl:min-h-[22rem] xl:items-stretch">
          <OiChanges hasAccess={hasPageAccess} />
          <AnalystActions />
        </div>
      </div>
    </div>
  );
}
